import * as React from 'react';
import Modal from 'react-bootstrap/Modal';
import axios from 'axios';
import { toast } from 'react-toastify';
import authService from './api-authorization/AuthorizeService';
import { EpaQuestions } from './models/JobInterfaces'
import { GenericList } from './models/JobSharedInterfaces';
import JobQuoteEpaQuestions from './JobQuoteEpaQuestions';

interface JobQuoteEpaQuestionsModalProps {
    showModal: boolean;
    isReadOnly: boolean;
    jobId: number;
    jobQuoteItemId: number;
    itemTypeId: number;
    itemId: number;
    item: string;
    hideModal(): void;
    saveModal(tankLidExposed: boolean, confinedSpace: boolean, weekendJob: boolean, emergencySameDayJob: boolean, updatedInvoiceDescription: string, updatedIncludeGreaseTrap: boolean): void;
}

const JobQuoteEpaQuestionsModalData = (props: JobQuoteEpaQuestionsModalProps) => {
    const [epaQuestions, setEpaQuestions] = React.useState<EpaQuestions>({
        tankLidExposed: -1,
        tankLidLocationKnown: -1,
        tankLidLocation: "",
        truckLargeAccess: -1,
        truckSmallAccess: -1,
        truckAccessToTank: -1,  //within 25m
        steepDriveway: -1,
        hoseMetres: 0,
        sealedDriveway: -1,
        lastService: "",
        waterBoard: "",
        waterAccessOnSite: -1,
        emergencySameDayJob: -1,
        weekendJob: -1,
        siteVisit: -1,
        confinedSpace: -1,
        materialSafetyDataSheet: -1,
        sampleTestingRequired: -1
    });
    const [yesNoOptions, setYesNoOptions] = React.useState<GenericList[]>([]);
    const [epaLidLocationFeeFormatted, setEpaLidLocationFeeFormatted] = React.useState("$0.00");
    const [weekendFeeFormatted, setWeekendFeeFormatted] = React.useState("$0.00");
    const [emergencyFeeFormatted, setEmergencyFeeFormatted] = React.useState("$0.00");
    const [invoiceDescription, setInvoiceDescription] = React.useState("");
    const [includeGreaseTrap, setIncludeGreaseTrap] = React.useState(false);
    const [saveDisabled, setSaveDisabled] = React.useState(false);

    React.useEffect(() => {
        if (props.jobQuoteItemId > 0) {
            getData();
        }
    }, [props.jobQuoteItemId]);

    const getData = async () => {
        const token = await authService.getAccessToken();

        //defaulting JobTypeId to 1 as that will set it to get ALL customers
        axios.get('api/Job/GetEpaQuestions?JobQuoteItemId=' + props.jobQuoteItemId, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            setEpaQuestions(res.data.epaQuestions);
            setYesNoOptions(res.data.yesNoOptions);
            setEpaLidLocationFeeFormatted(res.data.epaExtra30MinOnSiteFeeFormatted);
            setWeekendFeeFormatted(res.data.weekendFeeFormatted);
            setEmergencyFeeFormatted(res.data.emergencyFeeFormatted);
            setInvoiceDescription(res.data.invoiceDescription);
            setIncludeGreaseTrap(res.data.includeGreaseTrap);
            setSaveDisabled(false);
        });
    }

    const handleEpaQuestionChange = (fieldName:string, fieldValue: number) => {
        setEpaQuestions(prevState => ({ ...prevState, [fieldName]: fieldValue }));

        if (fieldName === "truckAccessToTank" || fieldName === "steepDriveway") {
            setEpaQuestions(prevState => ({ ...prevState, hoseMetres: 0 }));
        } else if (fieldName === "tankLidLocationKnown") {
            setEpaQuestions(prevState => ({ ...prevState, tankLidLocation: "" }));
        } else if (fieldName === "weekendJob") {
            setEpaQuestions(prevState => ({ ...prevState, emergencySameDayJob: -1 }));
        } else if (fieldName === "tankLidExposed") {
            setEpaQuestions(prevState => ({ ...prevState, tankLidLocationKnown: -1, tankLidLocation: "" }));
        }
    }

    const handleEpaQuestionTextChange = (fieldName: string, fieldValue: string) => {
        setEpaQuestions(prevState => ({ ...prevState, [fieldName]: fieldValue }));
    }

    const handleInvoiceDescriptionChange = (updatedInvoiceDescription: string) => {
        setInvoiceDescription(prevState => updatedInvoiceDescription);
    }

    const handleIncludeGreaseTrapChange = (updatedIncGreaseTrap: boolean) => {
        setIncludeGreaseTrap(prevState => updatedIncGreaseTrap);
    }

    const updateSaveDisabled = (saveDisUpdated: boolean) => {
        setSaveDisabled(saveDisUpdated);
    }

    const saveEpa = async () => {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        var saveData = {
            jobId: props.jobId,
            jobQuoteItemId: props.jobQuoteItemId,
            itemId: props.itemId,
            epaQuestions: epaQuestions,
            invoiceDescription: invoiceDescription,
            trolley: includeGreaseTrap,
            subId: user.sub
        };

        axios.post('api/Job/SaveEpaQuestions', saveData, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            if (res.data.isError) {
                var errors = res.data.messages as any[];
                errors.map(function (error: any) {
                    toast.error(error.content);
                });
                setSaveDisabled(false);
            } else {
                setSaveDisabled(false);
                toast.success("Questions Saved");
                props.saveModal(epaQuestions.tankLidExposed == 1, epaQuestions.confinedSpace == 1, epaQuestions.weekendJob == 1, epaQuestions.emergencySameDayJob == 1, invoiceDescription, includeGreaseTrap);
            }
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    return (
        <Modal size='lg' show={props.showModal} onHide={props.hideModal}>
            <Modal.Header className="modal__header--colour">
                <Modal.Title>EPA Questions</Modal.Title>
                <button type="button" className="close" onClick={props.hideModal}>&times;</button>
            </Modal.Header>
            <Modal.Body>
                <span className="makeitred">Note: Updating the questions may re-calcuate the price</span>
                <JobQuoteEpaQuestions isReadOnly={props.isReadOnly} showInvoiceDescription={true} itemTypeId={props.itemTypeId} epaLidLocationFeeFormatted={epaLidLocationFeeFormatted} weekendFeeFormatted={weekendFeeFormatted} emergencyFeeFormatted={emergencyFeeFormatted} epaQuestions={epaQuestions} invoiceDescription={invoiceDescription} includeGreaseTrap={includeGreaseTrap} yesNoOptions={yesNoOptions} showBack={false} saveDisabled={saveDisabled} updateNumber={handleEpaQuestionChange} updateText={handleEpaQuestionTextChange} updateInvoiceDescription={handleInvoiceDescriptionChange} updateIncGreaseTrap={handleIncludeGreaseTrapChange} updateSaveDisabled={updateSaveDisabled} save={saveEpa} />
            </Modal.Body>
        </Modal>
    )
}

export default JobQuoteEpaQuestionsModalData;

